import _ from 'lodash';

import Communicator from 'models/communicator';
import connect from 'components/lib/connect';
import {
  CONVERSATION,
  CUSTOMER_TYPING_INDICATOR,
  FILTERED_ITEMS,
  ITEM,
  LAST_CONVERSATION_SPACER,
  LAST_CONVERSATION_SPACER_ID,
} from './feed/feed_item_types';
import ConversationItemType from 'models/conversation_item_type';
import { getSortedConversations } from 'actions/conversation/lib/conversation_helpers';
import isItemStackable from './is_item_stackable';
import itemPassesFilter from './filtering/item_passes_filter';
import { RoutingChannel } from 'models/agent_routing_preferences';
import { routingChannelForInteractionType } from 'models/interaction_type';
import { SessionEndReason } from 'models/session_ended';
import TypingState from 'models/typing_state';

// SessionEndReasons that we actually display in the feed
const visibleSessionEndedReasons = Object.freeze([
  SessionEndReason.AGENT_ENDED,
  SessionEndReason.CUSTOMER_ABANDONED,
  SessionEndReason.CUSTOMER_NO_REPLY,
  SessionEndReason.STARTED_NEW,
  SessionEndReason.TIME_EXPIRED,
]);

/**
 * fetchFeedItems is a container HoC which retrieves conversations and items for a customers and creates
 * a list of sorted "feed items", which are lightweight representations of things we want to see in the feed,
 * (like conversation items or conversation headers).
 *
 * Avoiding instantiating item models was deliberate, as it becomes quite expensive to do that for hundreds of items,
 * especially when we want to avoid any scroll jank.
 */

function mapStateToProps({ getProvider, isFeatureEnabled }, props) {
  const conversationsProvider = getProvider('conversations');
  const conversationHistoryProvider = getProvider('conversationHistory');
  const itemIdsProvider = getProvider('itemIds');
  const currentAgentId = getProvider('currentAgent').get().id;
  const activeSessionProvider = getProvider('activeSessions');
  const conversationMergeProvider = getProvider('conversationMerge');
  const filterEnabled = isFeatureEnabled('conversationHistoryFilter');
  const filter = getProvider('conversationHistoryFilter').get();

  const conversationHistoryErrors = conversationHistoryProvider.getErrorForLoading();
  const conversationErrors = conversationsProvider.getErrorForLoading();
  const itemIdsErrors = itemIdsProvider.getErrorForLoading();

  const sortedConversations = getSortedConversations(conversationsProvider);
  let items = getItemsFromIds();

  const activeSession = activeSessionProvider.findBy({ customer: { id: props.customerId } });
  let customerIsTyping = _.get(activeSession, 'typingState.state') === TypingState.STARTED;

  if (customerIsTyping) {
    items.push({
      type: CUSTOMER_TYPING_INDICATOR,
      id: 'customer-typing',
    });
  }

  const hasNoConversations = !sortedConversations.length && !conversationsProvider.isLoading();
  const hasNoItems = !items.length && !itemIdsProvider.isLoading();
  const isLastConversationEmpty = items.length > 0 && _.last(items).type === CONVERSATION;

  items.push({
    type: LAST_CONVERSATION_SPACER,
    id: LAST_CONVERSATION_SPACER_ID,
    isEmpty: hasNoItems || isLastConversationEmpty,
  });

  return {
    customerId: props.customerId,
    filterEnabled,
    isEmpty: hasNoItems && hasNoConversations,
    hasErrorLoadingConversationHistory: !!conversationHistoryErrors,
    hasErrorLoadingConversations: !!conversationErrors,
    hasErrorLoadingItemIds: !!itemIdsErrors,
    items,
    isLoading:
      conversationsProvider.isLoading() ||
      conversationHistoryProvider.isLoading() ||
      itemIdsProvider.isLoading() ||
      conversationMergeProvider.isPending(),
  };

  function getItemsFromIds() {
    const items = [];

    let seenConvos = new Set();
    let previousItemImmutable = null;

    const filterActive = filterEnabled && filter.isActive();
    // Number of consecutive loaded items the filter has hidden since the last
    // visible item. Flushed into a FILTERED_ITEMS marker right before the next
    // visible item (and after the loop) so each hidden stretch shows one
    // consolidated "N items hidden" divider in the timeline.
    let hiddenRunCount = 0;
    // Newest item from someone else in that stretch, so the feed can tell whether the agent has read it.
    let hiddenRunNewestFromOthersTimestamp = null;

    const filteredItems = id => ({
      type: FILTERED_ITEMS,
      id,
      count: hiddenRunCount,
      newestFromOthersTimestamp: hiddenRunNewestFromOthersTimestamp,
    });

    let itemIds = _.sortBy(itemIdsProvider.findAll(), 'timestamp');
    for (let i = 0; i < itemIds.length; i++) {
      const { id, conversationId, timestamp } = itemIds[i];

      const itemImmutable = conversationHistoryProvider.immutableStore.binding.get(id);
      const initiatorId = itemImmutable && itemImmutable.getIn(['initiator', 'id']);
      const initiatorType = itemImmutable && itemImmutable.getIn(['initiator', 'type']);
      const initiatedByCurrentAgent = initiatorType === Communicator.AGENT && initiatorId === currentAgentId;

      if (itemImmutable && !shouldFeedItemBeCreated(itemImmutable)) {
        continue;
      }

      // Client-side filter: hide loaded items that don't match. Items whose
      // content hasn't loaded yet are kept as placeholders so scroll-driven
      // loading keeps running; they're re-evaluated once their content arrives.
      if (filterActive && itemImmutable && !itemPassesFilter(itemImmutable, filter)) {
        hiddenRunCount += 1;
        if (!initiatedByCurrentAgent) {
          hiddenRunNewestFromOthersTimestamp = timestamp;
        }
        continue;
      }

      // Flush the running count of hidden items into a single divider before
      // the next visible item, so agents see there's more content in the gap.
      if (hiddenRunCount > 0) {
        items.push(filteredItems(`${id}|filtered`));
        hiddenRunCount = 0;
        hiddenRunNewestFromOthersTimestamp = null;
      }

      // Insert the conversation header lazily, right before the first item we
      // actually display for it, so a conversation with no visible items
      // doesn't render a dangling header.
      if (conversationId && !seenConvos.has(conversationId)) {
        items.push({
          type: CONVERSATION,
          id: `${conversationId}|conversation`,
        });
        seenConvos.add(conversationId);
      }

      const newItem = {
        conversationId,
        id,
        initiatedByCurrentAgent,
        isStacked: isItemStackable(itemImmutable, previousItemImmutable),
        // The feed is sorted by this, and unlike `timestamp` it's known before the item's content loads.
        sortTimestamp: timestamp,
        // Null until the item's content loads, which is what FetchMoreItems uses to decide what to fetch.
        timestamp: itemImmutable ? itemImmutable.get('timestamp') : null,
        type: ITEM,
      };
      items.push(newItem);

      previousItemImmutable = itemImmutable;
    }

    // Trailing hidden stretch (the newest items are all filtered out): flush a
    // final divider so that gap is represented too.
    if (hiddenRunCount > 0) {
      items.push(filteredItems('filtered|trailing'));
    }

    // This usually only happens when we create a new conversation without any items in it.
    // Skip it while a filter is active so conversations with no matching items don't get a
    // dangling header re-inserted.
    if (!filterActive && seenConvos.size < sortedConversations.length) {
      const missingConversations = _.filter(sortedConversations, conversation => !seenConvos.has(conversation.id));
      _.forEach(missingConversations, conversation => {
        for (let i = items.length - 1; i >= 0; i--) {
          const item = items[i];
          if (item.timestamp && new Date(item.timestamp) <= new Date(conversation.createdAt)) {
            items.splice(i + 1, 0, {
              type: CONVERSATION,
              id: `${conversation.id}|conversation`,
            });
            return;
          }
        }
      });
    }

    return items;
  }
}

function shouldFeedItemBeCreated(itemImmutable) {
  const contentType = itemImmutable.getIn(['content', 'type']);

  switch (contentType) {
    case ConversationItemType.EMAIL_CAMPAIGN_OUTREACH_RESULT:
    case ConversationItemType.SMS_CAMPAIGN_OUTREACH_RESULT:
    case ConversationItemType.MESSAGING_SESSION:
      return false;
    case ConversationItemType.SESSION_ENDED:
      return !isMailSessionType() && _.includes(visibleSessionEndedReasons, itemImmutable.getIn(['content', 'reason']));
    default:
      return true;
  }

  function isMailSessionType() {
    const sessionType = itemImmutable.getIn(['content', 'sessionType']);
    return routingChannelForInteractionType(sessionType) === RoutingChannel.MAIL;
  }
}

export default connect(mapStateToProps);
